/**
 * Patron login page for the public portal.
 *
 * Accessibility (RGAA AA):
 * - Proper form labeling with htmlFor
 * - aria-describedby linking inputs to error messages
 * - aria-invalid on invalid fields
 * - aria-live for status announcements
 * - Visible focus indicators
 * - Show/hide password with accessible toggle
 */

import { useState, useCallback, useId } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { usePatronAuth } from "../contexts/PatronAuthContext";

export function PatronLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, isAuthenticated } = usePatronAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Generate unique IDs for accessibility
  const errorId = useId();
  const identifierId = useId();
  const passwordId = useId();

  // Get redirect path from location state
  const locationState = location.state as { from?: string; notice?: string } | null;
  const from = locationState?.from ?? "/opac/account";
  // Confirmation brought from another page (e.g. password just chosen)
  const notice = locationState?.notice;

  // Redirect if already authenticated
  if (isAuthenticated) {
    void navigate(from, { replace: true });
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!identifier.trim() || !password) {
        setError("Veuillez remplir tous les champs.");
        return;
      }

      try {
        await login(identifier.trim(), password);
        void navigate(from, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Une erreur est survenue.");
      }
    },
    [identifier, password, login, navigate, from]
  );

  const handleTogglePassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {/* Header */}
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <svg
                className="h-6 w-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Connexion</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Accédez à votre compte lecteur
            </p>
          </div>

          {notice && !error && (
            <div
              role="status"
              className="mb-4 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
            >
              {notice}
            </div>
          )}

          {/* Error message - announced by screen readers */}
          {error && (
            <div
              id={errorId}
              className="mb-4 rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
              role="alert"
              aria-live="assertive"
            >
              <span className="sr-only">Erreur : </span>
              {error}
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor={identifierId}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Numéro de carte ou adresse e-mail
                <span className="sr-only"> (obligatoire)</span>
              </label>
              <input
                id={identifierId}
                type="text"
                value={identifier}
                onChange={(e) => {
                  setIdentifier(e.target.value);
                }}
                placeholder="Ex: 1234567 ou jean.dupont@email.fr"
                autoComplete="username"
                autoFocus
                required
                aria-required="true"
                aria-invalid={error ? "true" : undefined}
                aria-describedby={error ? errorId : undefined}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label
                htmlFor={passwordId}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Mot de passe
                <span className="sr-only"> (obligatoire)</span>
              </label>
              <div className="relative">
                <input
                  id={passwordId}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  aria-required="true"
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby={error ? errorId : undefined}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleTogglePassword}
                  className="absolute top-1/2 right-3 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-gray-300"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <svg
                    className="h-5 w-5 animate-spin motion-reduce:animate-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Connexion en cours...</span>
                </span>
              ) : (
                "Se connecter"
              )}
            </button>

            {/* Live region for status updates */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {isLoading && "Connexion en cours, veuillez patienter."}
            </div>
          </form>

          {/* Help links */}
          <div className="mt-6 space-y-3 text-center text-sm">
            <Link
              to="/opac/acces-compte"
              className="block rounded text-blue-600 hover:text-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-blue-400 dark:hover:text-blue-300"
            >
              Mot de passe oublié ?
            </Link>
            <p className="text-gray-600 dark:text-gray-400">
              Première connexion ?{" "}
              <Link
                to="/opac/acces-compte?activation=1"
                className="rounded text-blue-600 hover:text-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-blue-400 dark:hover:text-blue-300"
              >
                Activez votre compte
              </Link>
            </p>
          </div>
        </div>

        {/* Back to catalog */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <Link
            to="/opac"
            className="rounded hover:text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:hover:text-blue-400"
          >
            ← Retour au catalogue
          </Link>
        </p>
      </div>
    </div>
  );
}

export default PatronLoginPage;
